import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Select,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useImportPreview, useStartImport } from '../hooks';
import type { AudienceImportPreviewResponse } from '@troia-v3/types';

export interface ImportCsvModalProps {
  isOpen: boolean;
  onClose: () => void;
  audienceId: string;
}

type Step = 'upload' | 'mapping' | 'starting';

const detectIdentifierColumn = (headers: string[]): string => {
  const found = headers.find((h) => {
    const lower = h.toLowerCase();
    return (
      lower.includes('phone') ||
      lower.includes('telefone') ||
      lower.includes('celular') ||
      lower.includes('email') ||
      lower.includes('e-mail')
    );
  });
  return found ?? headers[0] ?? '';
};

const detectNameColumn = (headers: string[]): string => {
  const found = headers.find((h) => {
    const lower = h.toLowerCase();
    return lower.includes('nome') || lower.includes('name');
  });
  return found ?? '';
};

export const ImportCsvModal: React.FC<ImportCsvModalProps> = ({
  isOpen,
  onClose,
  audienceId,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<AudienceImportPreviewResponse | null>(null);
  const [identifierColumn, setIdentifierColumn] = useState('');
  const [nameColumn, setNameColumn] = useState('');

  const previewMutation = useImportPreview(audienceId);
  const startMutation = useStartImport(audienceId);

  useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setFile(null);
      setPreview(null);
      setIdentifierColumn('');
      setNameColumn('');
    }
  }, [isOpen]);

  const headerOptions = useMemo(() => {
    if (!preview) return [];
    return preview.headers.map((h) => ({ value: h, label: h }));
  }, [preview]);

  const nameColumnOptions = useMemo(() => {
    return [{ value: '', label: '— Nenhum —' }, ...headerOptions];
  }, [headerOptions]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
  };

  const handleContinue = async () => {
    if (!file) return;
    try {
      const result = await previewMutation.mutateAsync(file);
      setPreview(result);
      setIdentifierColumn(detectIdentifierColumn(result.headers));
      setNameColumn(detectNameColumn(result.headers));
      setStep('mapping');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao ler arquivo');
    }
  };

  const handleStart = async () => {
    if (!file || !identifierColumn) return;
    setStep('starting');
    try {
      await startMutation.mutateAsync({
        file,
        identifierColumn,
        nameColumn: nameColumn || undefined,
      });
      toast.success('Importação iniciada');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao iniciar importação');
      setStep('mapping');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="3xl">
      {/* F74: título 18px via prop tipada do header */}
      <ModalHeader title="Importar CSV" onClose={onClose} />
      <ModalContent>
        {step === 'upload' && (
          <div className="space-y-4">
            <div
              className="rounded-lg border-2 border-dashed p-8 text-center"
              style={{ borderColor: '#e5e7eb' }}
            >
              <Icon
                name="upload"
                className="w-10 h-10 mx-auto mb-3"
                style={{ color: '#94a3b8' }}
              />
              <p className="text-sm mb-3" style={{ color: '#64748b' }}>
                Selecione um arquivo CSV com os destinatários
              </p>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                className="block mx-auto text-sm"
              />
              {file && (
                <p className="text-xs mt-3" style={{ color: '#1e293b' }}>
                  Arquivo selecionado: <span className="font-medium">{file.name}</span>
                </p>
              )}
            </div>
            <p className="text-xs" style={{ color: '#94a3b8' }}>
              O arquivo deve conter um cabeçalho na primeira linha. Apenas arquivos CSV são aceitos.
            </p>
          </div>
        )}

        {step === 'mapping' && preview && (
          <div className="space-y-4">
            <p className="text-sm" style={{ color: '#334155' }}>
              Selecione qual coluna contém o identificador (telefone ou email) e opcionalmente
              qual coluna contém o nome.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Coluna do identificador"
                value={identifierColumn}
                onChange={setIdentifierColumn}
                options={headerOptions}
                placeholder="Selecione..."
              />
              <Select
                label="Coluna do nome (opcional)"
                value={nameColumn}
                onChange={setNameColumn}
                options={nameColumnOptions}
              />
            </div>

            <div>
              <p className="text-xs font-medium mb-2" style={{ color: '#64748b' }}>
                Pré-visualização ({preview.rows.length} linhas)
              </p>
              <div
                className="overflow-auto max-h-64 rounded-lg border"
                style={{ borderColor: '#e5e7eb' }}
              >
                <table className="w-full text-xs">
                  <thead
                    style={{
                      backgroundColor: '#f8fafc',
                      borderBottom: '1px solid #e5e7eb',
                    }}
                  >
                    <tr>
                      {preview.headers.map((h) => (
                        <th
                          key={h}
                          className="text-left px-3 py-2 font-medium"
                          style={{
                            color:
                              h === identifierColumn || h === nameColumn
                                ? 'var(--theme-primary)'
                                : '#334155',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, rowIdx) => (
                      <tr
                        key={rowIdx}
                        style={{
                          borderBottom: rowIdx < preview.rows.length - 1 ? '1px solid #f1f5f9' : 'none',
                        }}
                      >
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx} className="px-3 py-2" style={{ color: '#64748b' }}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {step === 'starting' && (
          <div className="py-8 text-center">
            <Icon
              name="loader"
              className="w-8 h-8 mx-auto mb-3 animate-spin"
              style={{ color: 'var(--theme-primary)' }}
            />
            <p className="text-sm" style={{ color: '#334155' }}>
              Iniciando importação...
            </p>
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        {/* F71: Cancelar = Button secondary (bordado) em todo footer de modal */}
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
          disabled={step === 'starting'}
          data-testid="import-csv-cancel"
        >
          Cancelar
        </Button>
        {step === 'upload' && (
          <Button
            type="button"
            variant="primary"
            onClick={handleContinue}
            disabled={!file}
            loading={previewMutation.isPending}
            data-testid="import-csv-continue"
          >
            {previewMutation.isPending ? 'Lendo arquivo...' : 'Continuar'}
          </Button>
        )}
        {step === 'mapping' && (
          <Button
            type="button"
            variant="primary"
            onClick={handleStart}
            disabled={!identifierColumn}
            data-testid="import-csv-start"
          >
            Iniciar importação
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
